// apps/web/src/components/detail/TimelineTab.tsx
//
// Pure renderer for a synthesised event timeline. Pages own the synthesis
// of events from sparse fields (e.g. Vulnerability `firstSeen`/`lastUpdated`
// + patch availability; ThreatActor `lastSeen`; related articles'
// `publishedAt`). This component sorts events newest-first and renders them
// as a vertical list. Empty state when no events.
import { EmptyState } from '@/components/shared/EmptyState'
import { formatDate } from '@/lib/utils'

export interface TimelineEvent {
  id: string
  at: string
  kind: 'advisory' | 'exploit' | 'patch' | 'first-seen' | 'update' | 'mention' | 'other'
  label: string
  detail?: string
}

export interface TimelineTabProps {
  events: TimelineEvent[]
}

function capitalise(value: string): string {
  if (!value) return value
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function TimelineTab({ events }: TimelineTabProps) {
  if (events.length === 0) {
    return (
      <div data-testid="empty-state">
        <EmptyState title="No recorded events" />
      </div>
    )
  }

  // ISO-8601 strings sort lexicographically — so localeCompare gives correct
  // chronological order. DESC: newest first.
  const sorted = [...events].sort((a, b) => b.at.localeCompare(a.at))

  return (
    <ul className="relative border-l border-border pl-6">
      {sorted.map((event) => (
        <li key={event.id} className="mb-6">
          <div className="text-xs text-muted-foreground">{formatDate(event.at)}</div>
          <div className="text-sm font-medium text-foreground">{capitalise(event.kind)}</div>
          <div className="text-sm text-foreground">{event.label}</div>
          {event.detail && (
            <div className="mt-1 text-sm text-muted-foreground">{event.detail}</div>
          )}
        </li>
      ))}
    </ul>
  )
}
